import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../../components/firebase";
import { collection, query, orderBy, updateDoc, doc, getDoc, addDoc, onSnapshot, serverTimestamp } from "firebase/firestore";
import Swal from "sweetalert2";

const Leaves = () => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState("");
  const [leavesRecords, setLeavesRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState(""); // Empty = show all records
  const [filterEmail, setFilterEmail] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const checkUserRole = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          navigate("/");
          return;
        }

        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        
        if (userDocSnap.exists()) {
          const role = userDocSnap.data().role || "user";
          setUserRole(role);
          
          if (role !== "admin") {
            Swal.fire({
              toast: true,
              position: "top-end",
              icon: "error",
              title: "Access denied. Admin only.",
              timer: 2000,
            });
            navigate("/dashboard");
            return;
          }
        } else {
          navigate("/dashboard");
          return;
        }
      } catch (error) {
        console.error("Error checking user role:", error);
        navigate("/dashboard");
      }
    };

    checkUserRole();
  }, [navigate]);

  useEffect(() => {
    if (userRole === "admin") {
      setLoading(true);
      const q = query(collection(db, "leaves"), orderBy("appliedAt", "desc"));
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const records = [];
        snapshot.forEach((doc) => {
          records.push({ id: doc.id, ...doc.data() });
        });
        setLeavesRecords(records);
        setLoading(false);
      }, (error) => {
        console.error("Error fetching leaves records:", error);
        setLoading(false);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Failed to fetch leaves records",
          timer: 3000,
        });
      });

      return () => unsubscribe();
    }
  }, [userRole]);

  // Filtering logic
  const getFilteredRecords = () => {
    return leavesRecords.filter(record => {
      // Email filter
      if (filterEmail) {
        const searchStr = filterEmail.toLowerCase();
        const emailMatch = record.userEmail?.toLowerCase().includes(searchStr);
        const nameMatch = record.userName?.toLowerCase().includes(searchStr);
        if (!emailMatch && !nameMatch) return false;
      }
      
      // Status filter
      if (filterStatus !== "all" && record.status !== filterStatus) {
        return false;
      }

      // Date filter (check if filterDate falls between fromDate and toDate)
      if (filterDate) {
        const filterD = new Date(filterDate);
        filterD.setHours(0, 0, 0, 0);
        
        const fromD = new Date(record.fromDate);
        fromD.setHours(0, 0, 0, 0);
        
        const toD = new Date(record.toDate);
        toD.setHours(0, 0, 0, 0);
        
        if (filterD < fromD || filterD > toD) {
          return false;
        }
      }

      return true;
    });
  };

  const filteredRecords = getFilteredRecords();

  const handleApprove = async (record) => {
    const result = await Swal.fire({
      title: "Approve Leave?",
      text: "Are you sure you want to approve this leave request?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, approve it!"
    });

    if (result.isConfirmed) {
      try {
        await updateDoc(doc(db, "leaves", record.id), {
          status: "approved",
          actionBy: auth.currentUser?.email,
          actionAt: serverTimestamp()
        });
        
        // Create notification for employee
        try {
          await addDoc(collection(db, "notifications"), {
            type: "leave_response",
            leaveId: record.id,
            recipient: record.userEmail,
            title: "Leave Approved",
            body: `Your ${record.leaveType} leave request has been approved.`,
            createdBy: auth.currentUser?.email,
            createdByUID: auth.currentUser?.uid,
            createdAt: serverTimestamp(),
            seenBy: []
          });
        } catch (notifErr) {
          console.error("Error creating leave notification:", notifErr);
        }

        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Leave approved successfully",
          timer: 2000,
        });
      } catch (error) {
        console.error("Error approving leave:", error);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Failed to approve",
          timer: 2000,
        });
      }
    }
  };

  const handleReject = async (record) => {
    const result = await Swal.fire({
      title: "Reject Leave?",
      text: "Are you sure you want to reject this leave request?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, reject it!"
    });

    if (result.isConfirmed) {
      try {
        await updateDoc(doc(db, "leaves", record.id), {
          status: "rejected",
          actionBy: auth.currentUser?.email,
          actionAt: serverTimestamp()
        });
        
        // Create notification for employee
        try {
          await addDoc(collection(db, "notifications"), {
            type: "leave_response",
            leaveId: record.id,
            recipient: record.userEmail,
            title: "Leave Rejected",
            body: `Your ${record.leaveType} leave request has been rejected.`,
            createdBy: auth.currentUser?.email,
            createdByUID: auth.currentUser?.uid,
            createdAt: serverTimestamp(),
            seenBy: []
          });
        } catch (notifErr) {
          console.error("Error creating leave notification:", notifErr);
        }

        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Leave rejected successfully",
          timer: 2000,
        });
      } catch (error) {
        console.error("Error rejecting leave:", error);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Failed to reject",
          timer: 2000,
        });
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    if (status === "approved") {
      return (
        <div className="d-inline-flex align-items-center gap-2" style={{ backgroundColor: "rgba(34, 197, 94, 0.08)", color: "#15803d", padding: "6px 14px", borderRadius: "20px", fontWeight: "600", fontSize: "12px", border: "1px solid rgba(34, 197, 94, 0.2)" }}>
          <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#16a34a" }}></div> Approved
        </div>
      );
    }
    if (status === "rejected") {
      return (
        <div className="d-inline-flex align-items-center gap-2" style={{ backgroundColor: "rgba(239, 68, 68, 0.08)", color: "#b91c1c", padding: "6px 14px", borderRadius: "20px", fontWeight: "600", fontSize: "12px", border: "1px solid rgba(239, 68, 68, 0.2)" }}>
          <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#dc2626" }}></div> Rejected
        </div>
      );
    }
    return (
      <div className="d-inline-flex align-items-center gap-2" style={{ backgroundColor: "rgba(245, 158, 11, 0.08)", color: "#b45309", padding: "6px 14px", borderRadius: "20px", fontWeight: "600", fontSize: "12px", border: "1px solid rgba(245, 158, 11, 0.2)" }}>
        <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#d97706" }}></div> Pending
      </div>
    );
  };

  if (loading || userRole !== "admin") {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh", backgroundColor: "#fafaf9" }}>
        <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem", borderWidth: "0.2em", opacity: 0.7 }} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-3 p-md-5" style={{ backgroundColor: "#fafaf9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
      {/* Header Section (Minimalist & Breathable) */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-5 gap-4">
        <div className="d-flex align-items-center gap-4">
          <div>
            <h2 className="fw-bold mb-1" style={{ fontSize: isMobile ? "24px" : "32px", color: "#1e293b", letterSpacing: "-1px" }}>Manage Leaves</h2>
            <p className="text-muted mb-0" style={{ fontSize: "14px", letterSpacing: "0.5px" }}>Review and manage employee leave requests seamlessly.</p>
          </div>
        </div>
      </div>

      {/* Control Panel (Glassmorphism & Soft Shadows) */}
      <div className="bg-white rounded-4 shadow-sm p-3 mb-5" style={{ border: "1px solid #f1f5f9" }}>
        <div className="row g-3 align-items-center">
          
          <div className="col-12 col-md-5 d-flex gap-2 align-items-center">
            <div className="d-flex align-items-center bg-light rounded-pill p-1 flex-grow-1" style={{ border: "1px solid #e2e8f0" }}>
              <button
                className="btn btn-sm btn-light rounded-pill text-secondary transition-all"
                onClick={() => {
                  const current = filterDate ? new Date(filterDate) : new Date();
                  current.setDate(current.getDate() - 1);
                  setFilterDate(current.toISOString().split("T")[0]);
                }}
                style={{ width: "36px", height: "36px", border: "none", backgroundColor: "transparent" }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f1f5f9"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
              
              <input
                type="date"
                className="form-control bg-transparent border-0 text-center fw-semibold text-secondary shadow-none"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                style={{ fontSize: "14px", cursor: "pointer", letterSpacing: "0.5px" }}
              />
              
              <button
                className="btn btn-sm btn-light rounded-pill text-secondary transition-all"
                onClick={() => {
                  const current = filterDate ? new Date(filterDate) : new Date();
                  current.setDate(current.getDate() + 1);
                  setFilterDate(current.toISOString().split("T")[0]);
                }}
                style={{ width: "36px", height: "36px", border: "none", backgroundColor: "transparent" }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f1f5f9"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
            
            <button
              className="btn rounded-pill fw-medium px-4 shadow-sm transition-all"
              onClick={() => setFilterDate(new Date().toISOString().split("T")[0])}
              style={{ backgroundColor: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe", fontSize: "14px", height: "44px" }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#dbeafe"}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#eff6ff"}
            >
              Today
            </button>

            {filterDate && (
              <button
                className="btn rounded-pill btn-light text-muted px-3 transition-all"
                onClick={() => setFilterDate("")}
                style={{ height: "44px", border: "1px solid #e2e8f0", fontSize: "14px" }}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            )}
          </div>

          <div className="col-12 col-md-3">
            <select 
              className="form-select rounded-pill shadow-none fw-medium text-secondary" 
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ fontSize: "14px", height: "44px", border: "1px solid #e2e8f0", backgroundColor: "#f8fafc", cursor: "pointer" }}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending Requests</option>
              <option value="approved">Approved Leaves</option>
              <option value="rejected">Rejected Leaves</option>
            </select>
          </div>

          <div className="col-12 col-md-4">
            <div className="position-relative">
              <i className="bi bi-search position-absolute text-muted" style={{ left: "16px", top: "50%", transform: "translateY(-50%)" }}></i>
              <input
                type="text"
                className="form-control rounded-pill shadow-none"
                placeholder="Search employee email..."
                value={filterEmail}
                onChange={(e) => setFilterEmail(e.target.value)}
                style={{ fontSize: "14px", height: "44px", paddingLeft: "42px", border: "1px solid #e2e8f0", backgroundColor: "#f8fafc" }}
              />
            </div>
          </div>
          
        </div>
      </div>

      {/* Main Content Area */}
      {filteredRecords.length === 0 ? (
        <div className="d-flex flex-column justify-content-center align-items-center py-5" style={{ minHeight: "400px" }}>
          <div className="rounded-circle d-flex justify-content-center align-items-center mb-4" style={{ width: "80px", height: "80px", backgroundColor: "#f1f5f9" }}>
            <i className="bi bi-inbox text-secondary" style={{ fontSize: "32px", opacity: 0.5 }}></i>
          </div>
          <h4 className="fw-semibold text-secondary" style={{ letterSpacing: "-0.5px" }}>No leaves found</h4>
          <p className="text-muted" style={{ fontSize: "14px" }}>There are no leave records matching your current filters.</p>
        </div>
      ) : isMobile ? (
        <div className="row g-4">
          {filteredRecords.map((record) => (
            <div key={record.id} className="col-12">
              <div className="card border-0 rounded-4 shadow-sm transition-all" style={{ backgroundColor: "#fff", border: "1px solid #f1f5f9" }}>
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="rounded-circle d-flex justify-content-center align-items-center text-white fw-bold shadow-sm" style={{ width: "42px", height: "42px", backgroundColor: "#0f172a", fontSize: "16px" }}>
                        {(record.userName || record.userEmail)?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "15px", letterSpacing: "-0.2px" }}>
                          {record.userName || record.userEmail?.split("@")[0] || "N/A"}
                        </h6>
                        <small className="text-muted" style={{ fontSize: "12px" }}>{record.leaveType} Leave</small>
                      </div>
                    </div>
                    <div>{getStatusBadge(record.status)}</div>
                  </div>
                  
                  <div className="p-3 rounded-3 mb-3" style={{ backgroundColor: "#f8fafc", border: "1px dashed #cbd5e1" }}>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>From</span>
                      <strong className="text-dark" style={{ fontSize: "13px" }}>{formatDate(record.fromDate)}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>To</span>
                      <strong className="text-dark" style={{ fontSize: "13px" }}>{formatDate(record.toDate)}</strong>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <span className="text-muted d-block mb-1" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>Reason</span>
                    <p className="mb-0 text-dark" style={{ fontSize: "14px", lineHeight: "1.5", opacity: 0.8 }}>"{record.reason}"</p>
                  </div>

                  {record.status === "pending" && (
                    <div className="d-flex gap-2">
                      <button
                        className="btn flex-grow-1 rounded-pill fw-medium transition-all"
                        onClick={() => handleApprove(record)}
                        style={{ backgroundColor: "#dcfce7", color: "#16a34a", border: "1px solid #bbf7d0", fontSize: "14px" }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#22c55e"; e.currentTarget.style.color = "#fff"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#dcfce7"; e.currentTarget.style.color = "#16a34a"; }}
                      >
                        Approve
                      </button>
                      <button
                        className="btn flex-grow-1 rounded-pill fw-medium transition-all"
                        onClick={() => handleReject(record)}
                        style={{ backgroundColor: "#fee2e2", color: "#dc2626", border: "1px solid #fecaca", fontSize: "14px" }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#ef4444"; e.currentTarget.style.color = "#fff"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#fee2e2"; e.currentTarget.style.color = "#dc2626"; }}
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-4 shadow-sm overflow-hidden" style={{ border: "1px solid #f1f5f9" }}>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0" style={{ minWidth: "900px" }}>
              <thead>
                <tr>
                  <th className="border-0 bg-light text-muted fw-semibold py-3 px-4" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>Employee</th>
                  <th className="border-0 bg-light text-muted fw-semibold py-3 px-4" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>Leave Details</th>
                  <th className="border-0 bg-light text-muted fw-semibold py-3 px-4" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>Duration</th>
                  <th className="border-0 bg-light text-muted fw-semibold py-3 px-4" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>Status</th>
                  <th className="border-0 bg-light text-muted fw-semibold py-3 px-4 text-end" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>Actions</th>
                </tr>
              </thead>
              <tbody style={{ borderTop: "none" }}>
                {filteredRecords.map((record) => (
                  <tr key={record.id} className="transition-all" style={{ borderBottom: "1px solid #f8fafc", cursor: "default" }}>
                    <td className="py-4 px-4 border-0">
                      <div className="d-flex align-items-center gap-3">
                        <div className="rounded-circle d-flex justify-content-center align-items-center text-white fw-bold shadow-sm" style={{ width: "40px", height: "40px", backgroundColor: "#0f172a", fontSize: "15px" }}>
                          {(record.userName || record.userEmail)?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "14px", letterSpacing: "-0.2px" }}>
                            {record.userName || record.userEmail?.split("@")[0] || "N/A"}
                          </h6>
                          <small className="text-muted" style={{ fontSize: "12px" }}>{record.userEmail}</small>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4 border-0" style={{ maxWidth: "250px" }}>
                      <div className="d-flex flex-column gap-2 align-items-start">
                        <span className="badge rounded-pill" style={{ backgroundColor: "#f1f5f9", color: "#475569", fontWeight: "600", border: "1px solid #e2e8f0" }}>{record.leaveType} Leave</span>
                        <div className="text-muted text-truncate w-100" style={{ fontSize: "13px" }} title={record.reason}>"{record.reason}"</div>
                      </div>
                    </td>
                    <td className="py-4 px-4 border-0">
                      <div className="d-flex flex-column gap-1">
                        <div className="text-dark fw-medium" style={{ fontSize: "13px" }}>{formatDate(record.fromDate)}</div>
                        <div className="text-muted" style={{ fontSize: "12px" }}>to {formatDate(record.toDate)}</div>
                      </div>
                    </td>
                    <td className="py-4 px-4 border-0">
                      {getStatusBadge(record.status)}
                    </td>
                    <td className="py-4 px-4 border-0 text-end">
                      {record.status === "pending" ? (
                        <div className="d-flex gap-2 justify-content-end">
                          <button
                            className="btn btn-light rounded-circle d-flex align-items-center justify-content-center transition-all"
                            onClick={() => handleReject(record)}
                            title="Reject"
                            style={{ width: "36px", height: "36px", color: "#ef4444", border: "1px solid #fecaca" }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#fef2f2"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#f8f9fa"; }}
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                          <button
                            className="btn rounded-circle d-flex align-items-center justify-content-center transition-all"
                            onClick={() => handleApprove(record)}
                            title="Approve"
                            style={{ width: "36px", height: "36px", backgroundColor: "#dcfce7", color: "#16a34a", border: "1px solid #bbf7d0" }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#22c55e"; e.currentTarget.style.color = "#fff"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#dcfce7"; e.currentTarget.style.color = "#16a34a"; }}
                          >
                            <i className="bi bi-check-lg"></i>
                          </button>
                        </div>
                      ) : (
                        <div className="text-muted" style={{ fontSize: "12px" }}>
                          By {record.actionBy ? record.actionBy.split('@')[0] : "Admin"}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaves;
